import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Radio,
  Alert,
  Space,
  Progress,
  Typography,
  Descriptions,
} from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import type { ResetPasswordModalProps, ResetPasswordData } from '../types';
import { DesignTokens, getPasswordStrengthColor } from '../DesignTokens';
import {
  validatePassword,
  getPasswordStrength,
  maskPhone,
  generateRandomPassword,
} from '../utils';

const { Text, Link } = Typography;

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  user,
  onCancel,
  onReset,
}) => {
  const [form] = Form.useForm();
  const [method, setMethod] = useState<'sms' | 'admin'>('admin');
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<
    'weak' | 'medium' | 'strong'
  >('weak');
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    if (!user) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: ResetPasswordData = {
        userId: user.id,
        method,
        newPassword: values.newPassword,
        verifyCode: method === 'sms' ? values.verifyCode : undefined,
      };

      await onReset?.(data);
      form.resetFields();
      setPassword('');
      setPasswordStrength('weak');
    } catch (error) {
      console.error('重置密码失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordStrength(getPasswordStrength(pwd));
  };

  const handleGeneratePassword = () => {
    const randomPwd = generateRandomPassword();
    form.setFieldsValue({ newPassword: randomPwd, confirmPassword: randomPwd });
    setPassword(randomPwd);
    setPasswordStrength('strong');
  };

  const getStrengthPercent = (): number => {
    if (passwordStrength === 'weak') return 33;
    if (passwordStrength === 'medium') return 66;
    return 100;
  };

  const getStrengthText = (): string => {
    if (passwordStrength === 'weak') return '弱';
    if (passwordStrength === 'medium') return '中';
    return '强';
  };

  return (
    <Modal
      data-testid="user-modal-reset-password"
      title="重置密码"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={560}
      okText="确定重置"
      cancelText="取消"
      styles={{
        header: {
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.lg}px ${DesignTokens.spacing.xl}px`,
        },
        body: {
          padding: DesignTokens.spacing.xl,
        },
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="安全提示"
          description="重置密码将使用户当前密码失效，请谨慎操作"
          type="warning"
          showIcon
          icon={<SafetyOutlined />}
        />

        {user && (
          <Descriptions title="用户信息" column={1} size="small" bordered>
            <Descriptions.Item label="用户名">{user.username}</Descriptions.Item>
            <Descriptions.Item label="真实姓名">{user.realName}</Descriptions.Item>
            <Descriptions.Item label="手机号">
              {maskPhone(user.phone)}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Form form={form} layout="vertical">
          <Form.Item label="重置方式" name="method" initialValue="admin">
            <Radio.Group data-testid="user-radio-reset-method" onChange={(e) => setMethod(e.target.value)}>
              <Space direction="vertical">
                <Radio data-testid="user-radio-reset-admin" value="admin">管理员直接重置</Radio>
                <Radio data-testid="user-radio-reset-sms" value="sms">短信验证码重置</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {method === 'sms' && (
            <Form.Item
              label="验证码"
              name="verifyCode"
              rules={[{ required: true, message: '请输入验证码' }]}
            >
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  data-testid="user-input-verify-code"
                  placeholder="请输入6位验证码"
                  maxLength={6}
                  prefix={<SafetyOutlined />}
                />
                <button
                  data-testid="user-btn-send-code"
                  type="button"
                  style={{
                    padding: '4px 16px',
                    border: `1px solid ${DesignTokens.colors.gray5}`,
                    background: DesignTokens.colors.gray1,
                    cursor: 'pointer',
                  }}
                >
                  发送验证码
                </button>
              </Space.Compact>
            </Form.Item>
          )}

          <Form.Item
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>新密码</span>
                <Link data-testid="user-btn-generate-password" onClick={handleGeneratePassword} style={{ fontSize: 12 }}>
                  生成随机密码
                </Link>
              </div>
            }
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!validatePassword(value)) {
                    return Promise.reject(
                      new Error('密码至少8位，包含大小写字母和数字')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password
              data-testid="user-input-new-password"
              prefix={<LockOutlined />}
              placeholder="至少8位，包含大小写字母和数字"
              onChange={handlePasswordChange}
              maxLength={32}
            />
          </Form.Item>

          {password && (
            <div style={{ marginBottom: DesignTokens.spacing.lg }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    type="secondary"
                    style={{ ...DesignTokens.typography.bodySmall }}
                  >
                    密码强度:
                  </Text>
                  <Text
                    style={{
                      ...DesignTokens.typography.bodySmall,
                      color: getPasswordStrengthColor(passwordStrength),
                      fontWeight: 500,
                    }}
                  >
                    {getStrengthText()}
                  </Text>
                </div>
                <Progress
                  percent={getStrengthPercent()}
                  strokeColor={getPasswordStrengthColor(passwordStrength)}
                  showInfo={false}
                  size="small"
                />
              </Space>
            </div>
          )}

          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              data-testid="user-input-confirm-new-password"
              prefix={<LockOutlined />}
              placeholder="请再次输入新密码"
              maxLength={32}
            />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};
